import { Button, Tooltip } from "@altimateai/lego";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAllPlans } from "../../helpers/apis";
import styles from "./styles.module.scss";
import {
  CheckFAQ,
  DowngradeForm,
  EditionTag,
  InfoTooltip,
  getGradientLineColor,
  useCheckoutSessionRedirectMutation,
  useDowngradePlanMutation,
} from "./utils";
import { useIntercom } from "react-use-intercom";
import { default as SelectIcon } from "../../assets/icons/slider-select-icon.svg?react";
import { default as InfoDangerIcon } from "../../assets/icons/info_danger_icon.svg?react";
import { default as SelectedIcon } from "../../assets/icons/slider-selected-icon.svg?react";
import { default as SelectedIconDanger } from "../../assets/icons/slider-selected-icon-danger.svg?react";
import classNames from "classnames";
import { GET_ALL_SUBSCRIPTION_PLANS } from "./queryKey";
import { ComponentLoader } from "../../components/loader";
import { InfoMessages } from "./constants";
import { isOwner } from "@/services/auth";
import type { AllPlansResponse, CreditUsageResponse } from "./types";
import type { SuccessMessage } from "./utils";

type Plan = AllPlansResponse["plans"][number];
type DowngradeInfo = NonNullable<CreditUsageResponse["downgraded_info"]>;

const formatTokenAllowance = (tokens: number | string): string => {
  if (typeof tokens === "string") return "";
  if (tokens <= 0) return "";
  if (tokens >= 1_000_000) return `${tokens / 1_000_000}M`;
  if (tokens >= 1_000) return `${tokens / 1_000}K`;
  return String(tokens);
};

interface SelectTenureProps {
  currentTenure: string;
  setTenure: (tenure: string) => void;
}

const SelectTenure = ({ currentTenure, setTenure }: SelectTenureProps) => (
  <div className={styles.select_tenure}>
    <div
      className={classNames(styles.tenure, {
        [styles.tenure_selected]: currentTenure === "month",
      })}
      role="button"
      tabIndex={0}
      onClick={() => setTenure("month")}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") setTenure("month");
      }}
    >
      Monthly
    </div>
    <div
      className={classNames(styles.tenure, {
        [styles.tenure_selected]: currentTenure === "year",
      })}
      role="button"
      tabIndex={0}
      onClick={() => setTenure("year")}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") setTenure("year");
      }}
    >
      Yearly (Save 20%)
    </div>
  </div>
);

interface SmallLineProps {
  planState?: string;
}

const SmallLine = ({ planState = "" }: SmallLineProps) => (
  <div
    className={classNames(styles.select_line, {
      [styles.plan_upgrade]: planState === "upgrade",
      [styles.plan_downgrade]: planState === "downgrade",
    })}
  ></div>
);

interface CheckCircleProps {
  planState: string;
}

const CheckCircle = ({ planState }: CheckCircleProps) => (
  <div
    className={classNames(styles.select_circle, {
      [styles.plan_upgrade]: planState === "upgrade",
      [styles.plan_downgrade]: planState === "downgrade",
      [styles.default]: planState === "default",
    })}
  ></div>
);

interface GetSelectIconProps {
  planState?: string;
}

const GetSelectIcon = ({ planState = "" }: GetSelectIconProps) => {
  if (planState === "downgrade") return <SelectedIconDanger />;
  if (planState === "upgrade") return <SelectedIcon />;
  return <SelectIcon />;
};

interface DowngradeInfoTooltipProps {
  endDate: string;
  planName: string;
  cancelPlanDowngradeMutation: { mutate: () => void };
}

const DowngradeInfoTooltip = ({
  endDate,
  planName,
  cancelPlanDowngradeMutation,
}: DowngradeInfoTooltipProps) => (
  <Tooltip
    title={
      <div className={styles.downgrade_tooltip}>
        <div className={styles.downgrade_tooltip_text}>
          A downgrade is Scheduled at <span className={styles.highlight}>{endDate}</span> and the
          current plan will be downgraded to <span className={styles.highlight}>{planName}</span>.
        </div>
        <Button
          className={classNames(styles.button, {
            [styles.button_tooltip]: true,
          })}
          onClick={() => cancelPlanDowngradeMutation.mutate()}
        >
          <div className={styles.text}>Cancel Downgrade</div>
        </Button>
      </div>
    }
  >
    <div
      role="button"
      tabIndex={0}
      onClick={e => e.stopPropagation()}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") e.stopPropagation();
      }}
    >
      <InfoDangerIcon />
    </div>
  </Tooltip>
);

interface SliderProps {
  plans: Plan[];
  currentPlan: string;
  initialPlanName: string;
  setCurrentStatus: (plan: Plan) => void;
  initialExecutions: number | string;
  currentExecutions: number | string;
  planChangeState: string;
  tenureCheck: boolean;
  downgradeInfo: DowngradeInfo | null | undefined;
  currentTenure: string;
  cancelPlanDowngradeMutation: { mutate: () => void };
}

const Slider = ({
  plans,
  currentPlan,
  initialPlanName,
  setCurrentStatus,
  initialExecutions,
  currentExecutions,
  planChangeState,
  tenureCheck,
  downgradeInfo,
  currentTenure,
  cancelPlanDowngradeMutation,
}: SliderProps) => {
  const [hoveredPlan, setHoveredPlan] = useState<Plan | null>(null);

  const handleMouseOver = (_e: React.MouseEvent, plan: Plan) => {
    setHoveredPlan(plan);
  };

  return (
    <div className={styles.slider_container}>
      <div
        className={styles.slider_line}
        style={{
          background: getGradientLineColor(plans, currentPlan, initialPlanName, planChangeState),
        }}
      >
        {plans.map((plan, index) => (
          <div
            key={index}
            className={classNames(styles.plan_description, {
              [styles.default]:
                hoveredPlan &&
                hoveredPlan.plan_name === plan.plan_name &&
                currentPlan !== plan.plan_name,
              [styles.upgrade_plan]:
                currentPlan === plan.plan_name && planChangeState === "upgrade",
              [styles.downgrade_plan]:
                currentPlan === plan.plan_name && planChangeState === "downgrade",
            })}
            onMouseOver={e => handleMouseOver(e, plan)}
            onMouseOut={() => setHoveredPlan(null)}
            onFocus={() => setHoveredPlan(plan)}
            onBlur={() => setHoveredPlan(null)}
          >
            <div
              className={
                initialPlanName === plan.plan_name ||
                currentPlan === plan.plan_name ||
                (hoveredPlan && hoveredPlan.plan_name === plan.plan_name) ||
                (downgradeInfo &&
                  downgradeInfo.tenure === currentTenure &&
                  downgradeInfo.plan_name === plan.plan_name)
                  ? "mb-1"
                  : "mb-4"
              }
            >
              {plan.num_executions === "Custom" ? "Enterprise" : plan.num_executions}
            </div>
            <div
              className={styles.plan_checkbox}
              role="button"
              tabIndex={0}
              onClick={e => {
                e.stopPropagation();
                setCurrentStatus(plan);
              }}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.stopPropagation();
                  setCurrentStatus(plan);
                }
              }}
            >
              {initialPlanName === plan.plan_name ? (
                initialPlanName === currentPlan ? (
                  tenureCheck ? (
                    <SelectedIcon />
                  ) : (
                    <GetSelectIcon planState={planChangeState} />
                  )
                ) : (
                  <SelectIcon />
                )
              ) : currentPlan === plan.plan_name ? (
                <CheckCircle planState={planChangeState} />
              ) : downgradeInfo &&
                downgradeInfo.tenure === currentTenure &&
                downgradeInfo.plan_name === plan.plan_name ? (
                <DowngradeInfoTooltip
                  endDate={downgradeInfo.current_subscription_end_date}
                  planName={downgradeInfo.plan_display_name}
                  cancelPlanDowngradeMutation={cancelPlanDowngradeMutation}
                />
              ) : hoveredPlan && hoveredPlan.plan_name === plan.plan_name ? (
                <CheckCircle planState="default" />
              ) : (
                <SmallLine
                  planState={
                    (Number(plan.num_executions) < Number(initialExecutions) &&
                      Number(plan.num_executions) < Number(currentExecutions)) ||
                    (Number(plan.num_executions) > Number(initialExecutions) &&
                      Number(plan.num_executions) < Number(currentExecutions))
                      ? "upgrade"
                      : Number(plan.num_executions) > Number(currentExecutions) &&
                          Number(plan.num_executions) < Number(initialExecutions)
                        ? "downgrade"
                        : ""
                  }
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

interface SliderSelectProps {
  initialPlanName: string | undefined;
  initialTenure: string;
  currentPlan: string;
  setPlanName: (name: string) => void;
  planChangeState: string;
  setPlanChangeState: (state: string) => void;
  setShowSuccessMessage: (msg: SuccessMessage | undefined) => void;
  planToEditionMap: Record<string, string> | undefined;
  downgradeInfo: DowngradeInfo | null | undefined;
  cancelPlanDowngradeMutation: { mutate: () => void; isPending?: boolean };
}

const SliderSelect = ({
  initialPlanName = "",
  initialTenure,
  currentPlan,
  setPlanName,
  planChangeState,
  setPlanChangeState,
  setShowSuccessMessage,
  planToEditionMap = {},
  downgradeInfo,
  cancelPlanDowngradeMutation,
}: SliderSelectProps) => {
  const [showBuyPlanMessage, setShowBuyPlanMessage] = useState(false);
  const [showInfo, setShowInfo] = useState<SuccessMessage | undefined>(undefined);
  const [donwgradable, setDonwgradable] = useState(false);
  const [currentTenure, setCurrentTenure] = useState(initialTenure);
  const checkoutSessionRedirectMutation = useCheckoutSessionRedirectMutation();
  const downgradePlanMutation = useDowngradePlanMutation(
    setShowSuccessMessage,
    currentTenure,
    setPlanChangeState
  );
  const [executionCount, setExecutionCount] = useState<number | string>(100);
  const [initialexecutionCount, setInitialExecutionCount] = useState<number | string>(100);
  const [initialPrice, setInitialPrice] = useState<number | string>(0);
  const [currentPrice, setCurrentPrice] = useState<number | string>(0);
  const [initialTokenAllowance, setInitialTokenAllowance] = useState<number | string>(0);
  const [currentTokenAllowance, setCurrentTokenAllowance] = useState<number | string>(0);
  const [plans, setPlans] = useState<Plan[]>([]);
  const { show } = useIntercom();

  const {
    data: initialPlans,
    isLoading,
    error,
  } = useQuery({
    queryKey: [GET_ALL_SUBSCRIPTION_PLANS, currentTenure],
    queryFn: () => getAllPlans(currentTenure),
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (initialPlans) {
      setPlans(initialPlans.plans.filter(p => p.num_executions !== 500));
      setInitialExecutionCount(initialPlans.current_execuiton_count);
      setExecutionCount(initialPlans.current_execuiton_count);
      setInitialPrice(`$${initialPlans.current_price}`);
      const initPlan = initialPlans.plans.find(p => p.plan_name === initialPlanName);
      if (initPlan) {
        setInitialTokenAllowance(initPlan.token_allowance);
        setCurrentTokenAllowance(initPlan.token_allowance);
      }
    }
  }, [initialPlans]);

  const setCurrentStatus = (currPlan: Plan) => {
    let inf: SuccessMessage | undefined = {
      message: "",
      type: "",
    };
    setShowBuyPlanMessage(false);
    setDonwgradable(false);
    setPlanName(currPlan.plan_name);
    setCurrentPrice(`$${currPlan.price}`);
    setExecutionCount(currPlan.num_executions);
    setCurrentTokenAllowance(currPlan.token_allowance);
    if (currPlan.plan_name === "free100" && initialPlanName !== "free100") {
      setPlanChangeState("downgrade");
      inf.message = InfoMessages.cancelSubscription;
      inf.type = "warning";
    } else if (
      initialexecutionCount === currPlan.num_executions &&
      initialPlans &&
      currentTenure === initialPlans.current_tenure
    ) {
      setPlanChangeState("");
      inf = undefined;
    } else if (
      Number(String(initialPrice).substring(1)) * (initialTenure === "year" ? 12 : 1) >
      Number(currPlan.price) * (currentTenure === "year" ? 12 : 1)
    ) {
      setPlanChangeState("downgrade");
      inf.message = InfoMessages.downgradeSubscription;
      inf.type = "warning";
    } else {
      setPlanChangeState("upgrade");
      inf.message =
        initialPlanName === "free100"
          ? InfoMessages.upgradeSubscription
          : InfoMessages.upgradeSubscriptionProration;
      inf.type = "info_1";
    }
    setShowInfo(inf);
  };

  const setTenure = (tenure: string) => {
    setCurrentTenure(tenure);
    setPlanChangeState("");
    setShowInfo(undefined);
    setPlanName(initialPlanName);
    const initialCount = plans.find(plan => plan.plan_name === initialPlanName);
    if (initialCount) {
      setExecutionCount(initialCount.num_executions);
    }
  };

  if (error) return "An error has occurred: " + error.message;

  return (
    <>
      {isLoading && <ComponentLoader />}
      {plans && plans.length > 0 && (
        <div className={styles.purchase}>
          <div className={styles.top_bar}>
            <SelectTenure currentTenure={currentTenure} setTenure={setTenure} />
            <div className={styles.head_tags}>
              <CheckFAQ />
              <EditionTag editionName={planToEditionMap[currentPlan]} />
            </div>
          </div>
          <Slider
            plans={plans}
            currentPlan={currentPlan}
            initialPlanName={initialPlanName}
            setCurrentStatus={setCurrentStatus}
            initialExecutions={initialexecutionCount}
            currentExecutions={executionCount}
            planChangeState={planChangeState}
            tenureCheck={initialTenure === currentTenure}
            downgradeInfo={downgradeInfo}
            currentTenure={currentTenure}
            cancelPlanDowngradeMutation={cancelPlanDowngradeMutation}
          />
          <div className={styles.slide_bottom_container}>
            <div className={styles.plans_info}>
              <div className={styles.plan_info}>
                <div className="font-500">Current Subscription: ({initialTenure})</div>
                <div className="font-400">
                  {initialexecutionCount === "Custom"
                    ? "More credits per month"
                    : `${initialexecutionCount} Credits${formatTokenAllowance(initialTokenAllowance) ? ` + ${formatTokenAllowance(initialTokenAllowance)} tokens` : ""} for ${initialPrice} per month`}
                </div>
              </div>
              {planChangeState !== "" && (
                <div
                  className={classNames(styles.plan_info, {
                    [styles.plan_upgrade]: planChangeState === "upgrade",
                    [styles.plan_downgrade]: planChangeState === "downgrade",
                  })}
                >
                  <div className="font-500">New Subscription: ({currentTenure})</div>
                  <div className={classNames(styles.plan_cjange_text, "font-400")}>
                    {executionCount === "Custom"
                      ? "Custom Credits and Tokens per month"
                      : `${executionCount} Credits${formatTokenAllowance(currentTokenAllowance) ? ` + ${formatTokenAllowance(currentTokenAllowance)} tokens` : ""} for ${currentPrice} per month`}
                  </div>
                </div>
              )}
            </div>
            {currentPlan === "custom" || (initialPlans && !isOwner()) ? (
              <Button className={styles.button} onClick={show}>
                <div className={styles.text}>Contact Us!</div>
              </Button>
            ) : (
              <>
                {planChangeState === "" && downgradeInfo && (
                  <Button
                    className={styles.button}
                    onClick={() => cancelPlanDowngradeMutation.mutate()}
                  >
                    <div className={styles.text}>Cancel Downgrade</div>
                  </Button>
                )}
                {planChangeState === "downgrade" && (
                  <Button
                    className={classNames(styles.button, {
                      [styles.button_red]: donwgradable,
                      [styles.button_disabled]: !donwgradable,
                    })}
                    onClick={() => {
                      if (donwgradable)
                        downgradePlanMutation.mutate({
                          creditType: currentPlan,
                          tenure: currentTenure,
                        });
                    }}
                  >
                    <div className={styles.text}>Downgrade Plan</div>
                  </Button>
                )}
                {(planChangeState === "upgrade" ||
                  (!downgradeInfo && planChangeState !== "downgrade")) && (
                  <Button
                    className={styles.button}
                    onClick={() => {
                      if (
                        initialPlanName === currentPlan &&
                        (initialTenure === currentTenure || initialPlanName === "free100")
                      )
                        setShowBuyPlanMessage(true);
                      else
                        return checkoutSessionRedirectMutation.mutate({
                          creditType: currentPlan,
                          tenure: currentTenure,
                        });
                    }}
                  >
                    <div className={styles.text}>Buy Subscription</div>
                  </Button>
                )}
              </>
            )}
          </div>
          {(downgradePlanMutation.isPending ||
            checkoutSessionRedirectMutation.isPending ||
            cancelPlanDowngradeMutation.isPending) && <ComponentLoader />}
          {showBuyPlanMessage && (
            <InfoTooltip
              handleSuccessMessageClose={() => setShowBuyPlanMessage(false)}
              message="Move the slider above to select the credit count for a new subscription."
              type="info"
              showCloseButton={true}
              pageMessage={false}
            />
          )}
          {initialPlans && !isOwner() && (
            <InfoTooltip
              handleSuccessMessageClose={() => setShowInfo(undefined)}
              message="Only tenant owner can manage the subscriptions. Please contact us if you need any help."
              type="warning"
              showCloseButton={false}
              pageMessage={false}
            />
          )}
          {showInfo && (
            <InfoTooltip
              handleSuccessMessageClose={() => setShowInfo(undefined)}
              {...showInfo}
              pageMessage={false}
              showCloseButton={false}
            />
          )}
          {planChangeState === "downgrade" && !donwgradable && (
            <DowngradeForm setDonwgradable={setDonwgradable} />
          )}
        </div>
      )}
    </>
  );
};

export default SliderSelect;
